import "./DynamicTable.css";
import React from "react";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import LoadingStates from "shared/models/table/LoadingStates";
import classNames from "classnames";
import map from "lodash/map";
import isFunction from "lodash/isFunction";
import noop from "lodash/noop";
import Propulator from "higher-order/Propulator";
import Pagination from "../../../components/common/Pagination";
import autobind from "autobind-decorator";
import min from "lodash/min";
import isUndefined from "lodash/isUndefined";
import find from "lodash/find";
import SearchableText from "../../../components/tables/SearchableText";
import { TABLE_PAGE_SIZES } from "util/constants";
import { LoadingSpinner } from "shared/components/message/LoadSpinner";

@observer
class DynamicTable extends React.Component {
    static propTypes = {
        model: PropTypes.object.isRequired,
        showItemsPerPage: PropTypes.bool,
        className: PropTypes.string,
        dispatcher: PropTypes.object,
        showDefaultPagination: PropTypes.bool
    };

    static defaultProps = {
        showItemsPerPage: false,
        showDefaultPagination: true
    };

    componentDidMount() {
        this.props.model.onMount(this.props.dispatcher);
    }

    @autobind
    handlePageClick(e) {
        this.props.model.changePage(e.selected);
    }

    @autobind
    handlePageSizeSelect(e) {
        this.props.model.updateItemsPerPage(e.target.value, this.props.dispatcher);
    }

    handleColumnSortClick(columnDefinition, e) {
        e.preventDefault();
        this.props.model.updateSort(columnDefinition.sourceName, this.props.dispatcher);
    }

    renderTableHeader() {
        const columnOptions = this.props.model.columnOptions;
        return map(columnOptions, (column, index) => {
            // hack: porting old hidden column functionality
            if (column.hidden) {
                return null;
            }
            let cellClasses = {
                "table-header-cell": true,
                sortable: column.sortable,
                "column-hidden": this.props.model.recordCount === 0
            };
            cellClasses[column.sourceName] = true;
            const handleSort = column.sortable ? this.handleColumnSortClick.bind(this, column) : null;
            const currentSortDefinition = find(this.props.model.sorts, sort => sort.iteratee === column.sourceName);

            const isSorted = !isUndefined(currentSortDefinition);
            const isPrimary = isSorted && currentSortDefinition.primary;
            const isDescending = isSorted && currentSortDefinition.descending;
            const sortIconClasses = {
                "icon-up-dir": isPrimary && isDescending,
                "icon-down-dir": isPrimary && !isDescending,
                "icon-down-dir on-hover": !isPrimary
            };

            return (
                <div key={`column-header-${index}`} role="button" tabIndex={index} onKeyUp={handleSort} className={classNames(cellClasses)} onClick={handleSort}>
                    {isFunction(column.headerFormatter) ? column.headerFormatter(this.props.model) : (
                        <span title={column.displayTitle}>
                            {!isUndefined(column.displayName) ? column.displayName : column.sourceName}
                        </span>
                    )}
                    {column.sortable &&
                        <i className={classNames(sortIconClasses)} />
                    }
                </div>
            );
        });
    }

    renderTableRows() {
        const tableData = this.props.model.currentData || [];
        const rowOptions = this.props.model.rowOptions || {};
        if (tableData.length === 0 && this.props.model.loadState === LoadingStates.DATA_LOADED) {
            return this.renderEmptyTable();
        }

        return map(tableData, (rowModel, index) => {
            const rowData = rowModel.data;
            const key = isFunction(rowOptions.keyGetter) ? rowOptions.keyGetter(rowData) : `row-${index}`;
            const rowClasses = ["table-row", isFunction(rowOptions.classNameFormatter) ? rowOptions.classNameFormatter(rowData) : ""];
            const clickHandler = isFunction(rowOptions.onRowClick) ? rowOptions.onRowClick.bind(this, rowData) : noop;
            return (
                <div key={key} className={classNames(rowClasses)} role="link" tabIndex={index} onKeyUp={clickHandler} onClick={clickHandler}>
                    {this.renderSingleRow(rowModel, index, key)}
                </div>
            );
        });
    }

    renderSingleRow(rowModel, index, rowKey) {
        const data = rowModel.data;
        const columnOptions = this.props.model.columnOptions;
        const searchValue = this.props.model.searchFilter && this.props.model.searchFilter.searchValue ? this.props.model.searchFilter.searchValue : null;
        return map(columnOptions, column => {
            const dataElement = !isUndefined(data.get(column.sourceName)) ? data.get(column.sourceName) : null;

            // hack: porting old hidden column functionality
            if (column.hidden && !rowModel.toggledColumns.get(column.sourceName)) {
                return null;
            }

            let content = "";
            if (isFunction(column.formatter)) {
                content = column.formatter(dataElement, data, index, searchValue, this.props.model);
            } else if (column.searchable) {
                content = <SearchableText query={searchValue} text={dataElement.toString()} className="searchable-cell" />;
            } else {
                content = dataElement;
            }

            const cellClasses = classNames(["table-row-cell", column.sourceName, { "no-mouseflow": column.noMouseflow }]);
            return (
                <div key={`${rowKey}-value-${column.sourceName}`} className={cellClasses}>
                    {content}
                </div>
            );
        });
    }

    renderEmptyTable() {
        const emptyTableFormatter = this.props.model.emptyTableFormatter;
        if (isFunction(emptyTableFormatter)) {
            return emptyTableFormatter();
        }

        const rowClasses = classNames(["table-row", "no-results-row"]);
        return (
            <div className={rowClasses}>
                <div className="no-results-cell">
                    No results found.
                </div>
            </div>
        );
    }

    renderPagination() {
        const numberOfPages = this.props.model.pageCount;
        if (numberOfPages > 1) {
            return (
                <Pagination previousLabel={"prev"}
                    nextLabel={"next"}
                    pageCount={numberOfPages}
                    marginPagesDisplayed={1}
                    forcePage={this.props.model.currentPage || 0}
                    pageRangeDisplayed={4}
                    onPageChange={this.handlePageClick}
                    containerClassName={"pagination"}
                    subContainerClassName={"pages pagination"}
                    pageClassName={"page"}
                    previousClassName={"page"}
                    nextClassName={"page"}
                />
            );
        }
        return <div className="empty-pagination" />;
    }

    renderPageSizeSelect() {
        const pageSizeOptions = TABLE_PAGE_SIZES;
        if (!this.props.showItemsPerPage || this.props.model.recordCount <= min(pageSizeOptions)) {
            return null;
        }

        const pageSize = this.props.model.itemsPerPage;
        const selectClasses = classNames(["pagination-size-select", { "empty-pagination": this.props.model.recordCount <= pageSize }]);
        return (
            <div className={selectClasses}>
                <select onChange={this.handlePageSizeSelect} value={pageSize}>
                    {map(pageSizeOptions, option => {
                        return <option key={option} value={option}>{option}</option>;
                    })}
                </select>
                <span> per page</span>
            </div>
        );
    }

    render() {
        const { model } = this.props;
        return (
            <div className={classNames("custom-div-table", this.props.className)}>
                <div className="table-header">
                    {this.renderTableHeader()}
                </div>
                <div className="table-body">
                    {!model.isLoaded &&
                        <div className="loading-overlay">
                            <LoadingSpinner />
                        </div>
                    }
                    {model.isLoaded &&
                        this.renderTableRows()
                    }
                </div>
                {this.props.showDefaultPagination &&
                    <div className="pagination-container">
                        {this.renderPagination()}
                        {this.renderPageSizeSelect()}
                    </div>
                }
            </div>
        );
    }
}

export default Propulator(DynamicTable);
